import { LegalLayout, Section, Bullets } from "../components/LegalLayout";

const PlaidPrivacy = () => {
  return (
    <LegalLayout
      testid="plaid-privacy-page"
      title="Plaid Privacy Disclosure"
      subtitle="How CPC Pay uses Plaid to securely connect your bank account, what data Plaid accesses, and how you stay in control."
      effectiveDate="June 1, 2026"
    >
      <Section heading="1. Why CPC Pay Uses Plaid">
        <p>
          CPC Pay uses Plaid Inc. ("Plaid") to securely link your bank account so you can receive
          and move funds. Plaid acts as a trusted intermediary between CPC Pay and your bank — it
          lets you connect your account without ever sharing your online banking credentials with us.
        </p>
        <p>
          When you connect an account, you interact directly with Plaid Link, Plaid's secure
          connection widget. Plaid's handling of your information is governed by the{" "}
          <a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800" data-testid="plaid-eupp-link">
            Plaid End User Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section heading="2. What Information Plaid Accesses">
        <p>
          With your permission, and depending on the products CPC Pay uses, Plaid may collect and
          provide us the following from the financial account you connect:
        </p>
        <Bullets
          items={[
            "Account information — account and routing numbers, account type, and institution name.",
            "Account verification status — confirmation that you own and can access the account.",
            "Balances — available and current balance, used to verify funds before a transfer.",
            "Transactions — transaction history and details, where you enable that product.",
            "Account owner identity — name and contact details on file with your bank, used to verify the payee.",
          ]}
        />
        <p>
          CPC Pay stores the Plaid access token and account identifiers needed to operate your
          payments. We do <strong>not</strong> receive or store your bank username or password.
        </p>
      </Section>

      <Section heading="3. How Plaid Uses Your Data">
        <p>
          Plaid collects and processes your data to provide its connectivity services to CPC Pay,
          to operate and improve its services, and as otherwise described in the Plaid End User Privacy
          Policy. Plaid's use of your information is independent of, and in addition to, this
          disclosure and CPC Pay's own{" "}
          <a href="/privacy-policy" className="text-emerald-700 underline hover:text-emerald-800">
            Privacy Policy
          </a>
          .
        </p>
      </Section>

      <Section heading="4. Your Control & Rights">
        <Bullets
          items={[
            "You choose whether to connect a bank account and which account to link.",
            "You can disconnect your bank from CPC Pay at any time from the Bank Account page.",
            "You can view and manage the connections you've made with Plaid, and control your data, through the Plaid Portal.",
          ]}
        />
        <p>
          Manage your Plaid connections and data rights at{" "}
          <a href="https://my.plaid.com" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800" data-testid="plaid-portal-link">
            my.plaid.com
          </a>
          .
        </p>
      </Section>

      <Section heading="5. Data Security">
        <p>
          Bank connections are encrypted in transit and handled within Plaid's secure infrastructure.
          Because you authenticate directly with your bank through Plaid Link, your banking credentials
          are never exposed to CPC Pay. CPC Pay additionally protects your account with mandatory
          multi-factor authentication and audit logging.
        </p>
      </Section>

      <Section heading="6. Learn More">
        <Bullets
          items={[
            <>
              Plaid End User Privacy Policy:{" "}
              <a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">
                plaid.com/legal
              </a>
            </>,
            <>
              Plaid Portal (manage your connections):{" "}
              <a href="https://my.plaid.com" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">
                my.plaid.com
              </a>
            </>,
            <>
              CPC Pay Privacy Policy:{" "}
              <a href="/privacy-policy" className="text-emerald-700 underline hover:text-emerald-800">
                View here
              </a>
            </>,
          ]}
        />
      </Section>
    </LegalLayout>
  );
};

export default PlaidPrivacy;
